<template>
  <new-modal-dialog
    visible
    :persistent="true"
    max-width="90%"
    :scrollable="false"
    :fullscreen="$vuetify.breakpoint.smAndDown"
    container-class="pa-0 ma-0"
    card-class="pa-0 ma-0"
    :retain-focus="false"
    fluid
    light
  >
    <image-upload-dialog
      v-if="uploadDialog"
      :title="$t('title.image')"
      filename-prefix="newsletter"
      :help-label="$t('placeholder.image_upload')"
      :delete-confirmation-label="$t('message.confirm_delete_uploaded_image')"
      :max-width="500"
      @close="uploadDialog = false"
      @change="uploadImage"
    />
    <!-- mount point for email-editor -->
    <div
      id="email-editor-root"
      :class="{'editor--fullscreen': $vuetify.breakpoint.smAndDown}"
      class="w-100 pa-0 ma-0"
    />
    <!--  -->
    <v-divider style="position: relative; z-index: 10000;" />
    <template #actions>
      <v-spacer />
      <v-btn
        color="green"
        dark
        @click="save"
      >
        {{ saveBtnText }}
      </v-btn>
      <v-btn
        color="red"
        text
        @click="cancel"
      >
        {{ $t('button.cancel') }}
      </v-btn>
    </template>
  </new-modal-dialog>
</template>

<script setup lang="ts">
import {
  ref, PropType, getCurrentInstance, onBeforeUnmount, onMounted,
} from 'vue';
import { useScrollLock } from '@vueuse/core';
import i18n from '@/plugins/i18n';
import httpClient from '@/api/http-client';
import { performSaveAction, performDispatchAction } from '@/services/vue-utils';
import HtmlTemplate from '@/grpc-api/model/html-template';
import store from '@/store/store';
import NewModalDialog from '@/components/dialogs/NewModalDialog.vue';
import ReservationService from '@/grpc-api/model/reservation-service';
import { hostedWidgetURL } from '@/services/configuration';
import { loadStoreModule, unloadStoreModule } from '@/services/module-loader';
import ServiceManagerStore from '@/store/service-manager-store';
import { InitApp, DestroyApp } from '@guestplan/email-editor';
import ImageUploadDialog from '@/components/dialogs/ImageUploadDialog.vue';
import { cloneModel } from '@/model/model-utils';
import { TranslateResult } from 'vue-i18n';
import { useLanguages } from '../settings/helpers/settings-values';

/** Main content scroll lives here on most app screens (see `ToolbarContainer.vue`). */
const LAYOUT_SCROLL_CONTAINER = '.toolbar-container';

const services = ref([] as ReservationService[]);
const acctSettings = store.state.settings.account;

const props = defineProps({
  // TODO: why is template allowed to be undefined?
  template: { type: Object as PropType<HtmlTemplate | undefined>, required: false, default: undefined },
  templateLocale: { type: String as PropType<string | undefined>, required: false, default: undefined },
  saveBtnText: {
    type: [String, Object] as PropType<string|TranslateResult>, required: false, default: i18n.t('button.save'),
  },
});

const emit = defineEmits(['close', 'save']);

// image upload
const uploadDialog = ref(false);

let resolveUploadPromise: ((value: string) => void) | null = null;
let rejectUploadPromise: ((reason: any) => void) | null = null;

const onUpload = () => {
  uploadDialog.value = true;
  return new Promise<string>((resolve, reject) => {
    resolveUploadPromise = resolve;
    rejectUploadPromise = reject;
  });
};

const uploadImage = async (image: File | null) => {
  const ok = await performSaveAction(
    null,
    async () => {
      let imageUrl = '';
      // save image and get url, if undefined, image is being removed
      if (image !== null) {
        const key = store.state.settings.account.serviceManagerAccessKey ?? '';
        imageUrl = await httpClient.uploadImageFile(image, key);
      }

      resolveUploadPromise!(imageUrl);
    },
  );

  if (ok) {
    uploadDialog.value = false;
  } else {
    rejectUploadPromise!(new Error());
  }
};

const languages = useLanguages();

// Email Editor Callbacks
let toJsonExport = undefined as (() => string) | undefined;
let toHtmlExport = undefined as (() => string) | undefined;
let toHtmlPreview = undefined as (() => string) | undefined;
let toPlaintext = undefined as (() => string) | undefined;

const emailEditorCallbacks = {
  onUpload,
  toJsonExport: (cb: () => string) => { toJsonExport = cb; },
  toHtmlExport: (cb: () => string) => { toHtmlExport = cb; },
  toHtmlPreview: (cb: () => string) => { toHtmlPreview = cb; },
  toPlaintext: (cb: () => string) => { toPlaintext = cb; },
};

// /

const widgetLink = (s?: ReservationService) => {
  const { defaultAccessKey: apiKey } = store.state.settings.account;
  if (!apiKey) return undefined;
  if (!s) return hostedWidgetURL({ apiKey });

  const { locale, code: serviceCode } = s;
  if (!serviceCode) return undefined;
  return hostedWidgetURL({ apiKey, serviceCode, locale: s.translations ? props.templateLocale : locale });
};

const reset = async () => {
  services.value = store.getters.serviceManagertServices as ReservationService[];

  const getTranslation = (s: ReservationService) => {
    if (!s.translations) return undefined;

    const locale = props.templateLocale ?? store.getters.defaultLocale as string;

    return s.translations.find((t) => t.locale === locale)
    || s.translations.find((t) => t.locale === 'en')
    || s.translations[0];
  };

  const svcData = services.value.map((s) => ({
    id: s.code!,
    title: getTranslation(s)?.title ?? s.title,
    description: getTranslation(s)?.description ?? s.description,
    imageUrl: getTranslation(s)?.imageUrl ?? s.imageUrl,
    isActive: s.isActive,
    bookingLink: widgetLink(s) ?? '', // TODO: can this happen?
    locale: !getTranslation(s) ? languages.value.find((l) => l.locale === s.locale)?.language : undefined,
  }));
  // console.log(svcData);

  const emailEditorData = {
    services: svcData,
    widgetLink: widgetLink() ?? '', // TODO: can this happen?
    logoUrl: acctSettings.logoLink,
    contactDetails: {
      name: acctSettings.accountName ?? '',
      address: `${acctSettings.streetAddress}, ${acctSettings.postalCode} ${acctSettings.city}`,
      phone: acctSettings.phone,
      email: acctSettings.email,
      website: acctSettings.website,
      socials: {
        facebook: acctSettings.facebook,
        instagram: acctSettings.instagram,
        yelp: acctSettings.yelp,
        x: acctSettings.x,
        youtube: acctSettings.youtube,
        tripadvisor: acctSettings.tripadvisor,
        tiktok: acctSettings.tiktok,
      },
    },
  };

  const lang = i18n.locale.split('-')[0];
  const documentLang = props.templateLocale ?? store.state.settings.account.locale ?? lang;
  const devMode = !import.meta.env.PROD;
  InitApp(
    document.getElementById('email-editor-root')!,
    emailEditorData,
    emailEditorCallbacks,
    lang,
    documentLang,
    devMode,
    JSON.parse(props.template?.design ?? '{}'),
  );
};

const load = async () => {
  if (!store.getters.isServiceManagerLoaded) {
    await performDispatchAction('loadServiceManager', {}, { streamLoad: true }) as ReservationService[] | null;
  }
  reset();
};

const save = async () => {
  const design = toJsonExport!();
  console.log(design);
  const html = toHtmlExport!();
  console.log(html);
  const plaintext = toPlaintext!();
  console.log(plaintext);
  const preview = toHtmlPreview!();
  console.log(preview);

  let template: HtmlTemplate;
  if (props.template) {
    template = cloneModel(props.template);
    // template.name = 'edited name'
    template.design = design;
    template.html = html;
    template.plaintext = plaintext;
    template.preview = preview;
  } else {
    template = new HtmlTemplate({
      id: 0,
      // name = 'edited name',
      name: i18n.t('label.new_template') as string,
      design,
      html,
      plaintext,
      preview,
    });
  }

  emit('save', template);
};

const cancel = () => emit('close');

const documentScrollLocked = useScrollLock(document.documentElement, false);
onMounted(() => {
  documentScrollLocked.value = true;
  loadStoreModule(getCurrentInstance()?.proxy!, 'serviceManager', ServiceManagerStore, async () => { load(); });
});

onBeforeUnmount(() => {
  documentScrollLocked.value = false;
  DestroyApp();

  unloadStoreModule(
    getCurrentInstance()?.proxy!,
    'serviceManager',
    // no reset, reset perfomed when unloading marketing campaign manager store
    async () => {}, // store.dispatch('resetServiceManager');
  );
});

</script>

<style lang="scss">

#email-editor-root {
  --sidebar__height: calc(90vh - 52px);
  --container__height: calc(90vh - 54px);

  position: relative;
  height: calc(90vh - 54px);
  overflow: hidden;

  &.editor--fullscreen {
    height: calc(100vh - 54px);
    --sidebar__height: calc(100vh - 52px);
    --container__height: calc(100vh - 54px);
  }
}
</style>
